import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Pages } from '../Pages'
import { MenuItems } from '../enums/MenuItems'
import tenantApi from '../api/tenant/TenantApi'
import UserRolePill from '../components/UserRolePill'
import { UserDto, UserRoles } from '../api/tenant/models/UserDto'
import SearchPeopleModal from '../components/SearchPeopleModal'
import { PersonDto } from '../api/person/models/PersonDto'
import PersonUi from '../components/PersonUi'
import ToggleSwitch from '../components/ToggleSwitch'
import LayoutSingleView from '../components/layout/LayoutSingleView'

function UpdateUser(): React.JSX.Element {
    const [isSearchPersonModalOpen, setIsSearchPersonModalOpen] =
        useState(false)
    const [linkedPerson, setLinkedPerson] = useState<PersonDto>()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const userId = searchParams.get('userId')
    const [user, setUser] = useState<UserDto>()
    const [selectedRoles, setSelectedRoles] = useState<UserRoles[]>([])

    const refreshUser = useCallback(async () => {
        if (!userId) {
            return
        }

        const user = (await tenantApi.getUser(userId)).user
        setUser(user)
        setSelectedRoles(user.userRoles || [])
        setLinkedPerson(user.person || undefined)
    }, [userId])

    useEffect(() => {
        refreshUser()
    }, [userId, refreshUser])

    function handleRoleChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ): void {
        const role = event.target.value as UserRoles
        if (event.target.checked) {
            setSelectedRoles((prev) => [...prev, role])
        } else {
            setSelectedRoles((prev) => prev.filter((r) => r !== role))
        }
    }

    async function saveUser() {
        if (!user) {
            throw new Error(`Unable to save user, user not found.`)
        }

        await tenantApi.updateUser({
            userId: user.id,
            isActive: user.isActive,
            userRoles: selectedRoles,
            personId: linkedPerson?.id,
        })
        navigate(`${Pages.Tenant}?id=${user.tenantId}`)
    }

    async function handleSelectPerson(person: PersonDto) {
        setLinkedPerson(person)
        setIsSearchPersonModalOpen(false)
    }

    async function handleRemovePerson() {
        setLinkedPerson(undefined)
        setIsSearchPersonModalOpen(false)
    }

    function toggleActive() {
        if (user) {
            setUser({ ...user, isActive: !user.isActive })
        }
    }

    if (!user) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Tenants}>
                <div>Loading...</div>
            </LayoutSingleView>
        )
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Tenants}>
            <div>
                <h1>Edit User for {user.tenant?.name}</h1>
                <div>
                    <div>
                        <label htmlFor="email">Email*</label>
                        <input
                            id="email"
                            type="text"
                            placeholder="Email..."
                            value={user.email}
                            disabled
                            className="form-input"
                        ></input>
                    </div>
                    <div className="space-y-2 mt-4">
                        {[UserRoles.ADMIN, UserRoles.HM, UserRoles.TA].map(
                            (role) => (
                                <div key={role}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            id={role}
                                            name="roles"
                                            value={role}
                                            checked={selectedRoles.includes(
                                                role,
                                            )}
                                            onChange={handleRoleChange}
                                            className="mr-2"
                                        />
                                        <UserRolePill value={role} />
                                    </label>
                                </div>
                            ),
                        )}
                    </div>
                    <div className="mt-4">
                        <label htmlFor="isActive">Is Active</label>
                        <ToggleSwitch
                            checked={user.isActive}
                            onChange={toggleActive}
                        />
                    </div>
                    <div className="mt-4">
                        <label htmlFor="personId">
                            Link to Person (imported from Linked In)
                        </label>
                        {linkedPerson && (
                            <div>
                                <div>
                                    Selected person:{' '}
                                    <PersonUi person={linkedPerson} />
                                </div>
                                <button
                                    className="remove-button"
                                    onClick={handleRemovePerson}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                        <div>
                            <button
                                className="secondary-button"
                                onClick={() => setIsSearchPersonModalOpen(true)}
                            >
                                Select Person
                            </button>
                        </div>
                    </div>
                    <div className="form-buttons">
                        <button
                            className="primary-button mt-4"
                            onClick={saveUser}
                        >
                            Save
                        </button>
                        <button
                            className="danger-button"
                            onClick={() =>
                                navigate(`${Pages.Tenant}?id=${user.tenantId}`)
                            }
                        >
                            Cancel
                        </button>
                    </div>
                </div>
                {isSearchPersonModalOpen && (
                    <SearchPeopleModal
                        onClose={() => setIsSearchPersonModalOpen(false)}
                        onSelect={handleSelectPerson}
                    />
                )}
            </div>
        </LayoutSingleView>
    )
}

export default UpdateUser
